import { isNil } from 'lodash';
import { useCallback } from 'react';
import { View as RNView, ScrollView, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { IconButtonV2 } from '@shared/components/MBButton';
import { MBView } from '@shared/components/MBView';
import { Text } from '@shared/components/Text';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { useCardBlockScrollContext } from '@shared/components/blocks/contexts/scroll';
import { BatchEditContextProvider } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { MBChatInput } from '@shared/scenes/chat/input/containers/input';
import { CardFormTypeContext } from '@shared/scenes/entity/cardFormTypeContext';
import { EntityForm } from '@shared/scenes/entity/components/EntityForm';
import { FormErrors } from '@shared/scenes/entity/components/FormErrors';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { SubformNavigationContext } from '@shared/scenes/entity/components/fields/Subform/SubformEditField';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { grid } from '@shared/styles';
import { Colors } from '@shared/styles/colors';
import { API } from '@shared/types/api';
import { useIsDesktop, useIsMobile } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { ThreadContainer } from './ThreadContainer';

interface Props {
  fields: FieldRecord | null;
  updateField: UpdateField;
  components: API.AnyComponent[];
  errors: CardEntityError[];
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  entityId: string;
  threadId?: string;
  channelSlug?: string;
  users?: API.ShortUserFragment[];
  groups?: API.ChannelGroupFragment[];
  style?: StyleProp<ViewStyle>;
  isSidebarOpen?: boolean;
  closeSidebar?: () => void;
  saveAttempted?: boolean;
}

export function CardEntityEditForm({
  fields,
  updateField,
  components,
  errors,
  workflowTemplateId,
  viewTemplateId,
  entityId,
  threadId,
  channelSlug,
  users,
  groups,
  style = {},
  isSidebarOpen,
  closeSidebar,
  saveAttempted,
}: Props) {
  const isResponsiveForms = useResponsiveFormContext();
  const isDesktop = useIsDesktop();

  if (isResponsiveForms && isDesktop) {
    return (
      <FormWithChatSidebar
        components={components}
        fields={fields}
        updateField={updateField}
        errors={errors}
        workflowTemplateId={workflowTemplateId}
        viewTemplateId={viewTemplateId}
        entityId={entityId}
        threadId={threadId}
        channelSlug={channelSlug}
        users={users}
        groups={groups}
        style={style}
        isSidebarOpen={isSidebarOpen}
        closeSidebar={closeSidebar}
        saveAttempted={saveAttempted}
      />
    );
  } else {
    return (
      <FormWithBottomChat
        components={components}
        fields={fields}
        updateField={updateField}
        errors={errors}
        workflowTemplateId={workflowTemplateId}
        viewTemplateId={viewTemplateId}
        entityId={entityId}
        threadId={threadId}
        channelSlug={channelSlug}
        users={users}
        groups={groups}
        style={style}
        saveAttempted={saveAttempted}
      />
    );
  }
}

export function FormWithBottomChat({
  fields,
  updateField,
  components,
  errors,
  workflowTemplateId,
  viewTemplateId,
  entityId,
  threadId,
  channelSlug,
  users,
  groups,
  style = {},
  saveAttempted,
}: Props) {
  const isResponsiveForms = useResponsiveFormContext();
  const { isComponentFocused } = useComponentFocus();
  const scrollView = useCardBlockScrollContext();
  const scrollToEnd = useCallback(() => {
    scrollView?.current?.scrollToEnd();
  }, [scrollView]);

  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();

  const showChat = !isNil(threadId) && !isNil(users) && !isNil(groups) && !isNil(channelSlug);
  const showChatInput = showChat && (isDesktop || !isComponentFocused);

  if (isNil(fields) || isNil(workflowTemplateId) || isNil(viewTemplateId)) {
    return null;
  }

  return (
    <CardFormTypeContext.Provider value="EDIT">
      <BatchEditContextProvider>
        <CardRenderContext.Provider value="form">
          <SubformNavigationContext
            fields={fields}
            updateField={updateField}
            saveAttempted={saveAttempted}
            apiErrors={errors}
          >
            <FormErrors errors={errors} />
            <ScrollView
              ref={scrollView}
              style={[{ width: '100%' }, isResponsiveForms && styles.formPaneResponsive]}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode={isDesktop ? 'none' : 'on-drag'}
              contentContainerStyle={{ flexGrow: 1 }}
            >
              <EntityForm
                fields={fields}
                updateField={updateField}
                components={components}
                errors={errors}
                workflowTemplateId={workflowTemplateId}
                viewTemplateId={viewTemplateId}
                entityId={entityId}
                style={style}
              />
              {showChat && <ThreadContainer threadId={threadId} channelSlug={channelSlug} />}
            </ScrollView>
            {showChatInput && (
              <RNView
                style={[
                  styles.webContent,
                  isMobile && styles.mobileWebContent,
                  isResponsiveForms && styles.responsiveFormsBottomChatInput,
                ]}
              >
                <MBChatInput
                  placeholder={i18n('chat.input.placeholder')}
                  draftId={`thread:${threadId}`}
                  users={users}
                  groups={groups}
                  onSend={scrollToEnd}
                  autoFocus={false}
                />
              </RNView>
            )}
          </SubformNavigationContext>
        </CardRenderContext.Provider>
      </BatchEditContextProvider>
    </CardFormTypeContext.Provider>
  );
}

export function FormWithChatSidebar({
  fields,
  updateField,
  components,
  errors,
  workflowTemplateId,
  viewTemplateId,
  entityId,
  threadId,
  channelSlug,
  users,
  groups,
  style = {},
  isSidebarOpen,
  closeSidebar,
  saveAttempted,
}: Props) {
  const isResponsiveForms = useResponsiveFormContext();
  const scrollView = useCardBlockScrollContext();
  const scrollToEnd = useCallback(() => {
    scrollView?.current?.scrollToEnd();
  }, [scrollView]);

  const debouncedScrollToEnd = useCallback(() => {
    setTimeout(scrollToEnd, 100);
    setTimeout(scrollToEnd, 1000);
  }, [scrollToEnd]);

  const isDesktop = useIsDesktop();
  const showForm = isDesktop || !isSidebarOpen;
  const chatEnabled = !isNil(threadId) && !isNil(users) && !isNil(groups) && !isNil(channelSlug);
  const showChat = (isDesktop && isSidebarOpen) || isSidebarOpen;

  if (isNil(fields) || isNil(workflowTemplateId) || isNil(viewTemplateId)) {
    return null;
  }

  return (
    <CardFormTypeContext.Provider value="EDIT">
      <BatchEditContextProvider>
        <CardRenderContext.Provider value="form">
          <SubformNavigationContext
            fields={fields}
            updateField={updateField}
            saveAttempted={saveAttempted}
            apiErrors={errors}
          >
            <RNView style={[styles.responsivePanes, isDesktop && styles.responsivePanesDesktop]}>
              {showForm && (
                <RNView style={[styles.formPane, isResponsiveForms && styles.formPaneResponsive]}>
                  <FormErrors errors={errors} />
                  <ScrollView
                    style={{ width: '100%' }}
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode={isDesktop ? 'none' : 'on-drag'}
                    contentContainerStyle={{ flexGrow: 1 }}
                  >
                    <EntityForm
                      fields={fields}
                      updateField={updateField}
                      components={components}
                      errors={errors}
                      workflowTemplateId={workflowTemplateId}
                      viewTemplateId={viewTemplateId}
                      entityId={entityId}
                      style={style}
                    />
                  </ScrollView>
                </RNView>
              )}
              {chatEnabled && (
                <RNView
                  style={[
                    isDesktop && styles.rightPane,
                    !showChat && styles.rightPaneHidden,
                    !isDesktop && styles.chatPaneFullWidth,
                  ]}
                >
                  {isDesktop && (
                    <MBView row centerVertically style={styles.rightPaneHeader}>
                      <Text size="body1" weight="semibold">
                        {i18n('card.rightPane.chat.title')}
                      </Text>
                      <IconButtonV2
                        name="exit"
                        size={grid(3)}
                        color={Colors.v2.purple}
                        onPress={closeSidebar}
                      />
                    </MBView>
                  )}
                  <ScrollView
                    ref={scrollView}
                    style={styles.chatThreadScrollView}
                    keyboardShouldPersistTaps="handled"
                  >
                    <ThreadContainer
                      threadId={threadId}
                      channelSlug={channelSlug}
                      onThreadUpdated={debouncedScrollToEnd}
                      showHeader={!isDesktop}
                    />
                  </ScrollView>
                  <MBChatInput
                    placeholder={i18n('chat.input.placeholder')}
                    draftId={`thread:${threadId}`}
                    users={users}
                    groups={groups}
                    autoFocus={false}
                    onSend={scrollToEnd}
                    paddingHorizontal={1.5}
                  />
                </RNView>
              )}
            </RNView>
          </SubformNavigationContext>
        </CardRenderContext.Provider>
      </BatchEditContextProvider>
    </CardFormTypeContext.Provider>
  );
}

const styles = StyleSheet.create({
  content: {
    paddingTop: grid(1),
    paddingBottom: grid(8),
  },
  webContent: {
    width: '100%',
    paddingHorizontal: grid(4),
    alignSelf: 'center',
  },
  mobileWebContent: {
    paddingHorizontal: 0,
  },
  responsivePanes: {
    flexDirection: 'row',
    flex: 1,
  },
  responsivePanesDesktop: {
    borderTopWidth: 1,
    borderColor: Colors.v2.gray10,
  },
  formPane: {
    flex: 1,
    minWidth: 420,
  },
  formPaneResponsive: {
    backgroundColor: Colors.v2.backgroundGrayLight,
  },
  rightPane: {
    maxWidth: 400,
    flex: 1,
    borderLeftWidth: 1,
    borderColor: Colors.v2.gray10,
  },
  rightPaneHidden: {
    display: 'none',
  },
  rightPaneHeader: {
    paddingHorizontal: grid(2),
    paddingVertical: grid(1.5),
    borderBottomWidth: 1,
    borderColor: Colors.v2.gray10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rightPaneHeaderArrowIcon: {
    paddingHorizontal: grid(1.25),
    paddingTop: grid(0.5),
  },
  chatPaneFullWidth: {
    flex: 1,
  },
  chatThreadScrollView: {
    flex: 1,
    paddingRight: grid(1),
    borderBottomColor: Colors.v2.gray10,
    borderBottomWidth: 1,
  },
  responsiveFormsBottomChatInput: {
    borderTopWidth: 1,
    borderColor: Colors.v2.gray10,
  },
});
